import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FilterContent } from './FilterSidebar';
import {
  Search,
  Plus,
  MessageSquare,
  Clock,
  CheckCircle,
  XCircle,
  ChevronRight,
  User,
  Star,
  Send,
  ArrowLeft,
  ChevronLeft,
  Mail,
  Phone,
  Calendar,
  Tag,
  Menu,
  X,
  RefreshCw,
  FileText,
  PlusCircle,
  Target,
  TrendingUp,
  Zap,
  AlertTriangle,
  AlertCircle,
  Edit,
  Trash2,
  Save,
  Instagram
} from 'lucide-react';
import ApiService from '../../services/ApiService';
import useDebounce from '../../hooks/useDebounce';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import IntegrationInbox from '../ui/IntegrationInbox';

dayjs.extend(relativeTime);

const QueryItem = memo(function QueryItem({ query, isSelected, onSelect, getStatusStyle, getPriorityColor, instagramAccounts }) {
  const status = getStatusStyle(query);
  const priorityColor = getPriorityColor(query);

  // Find the associated Instagram account if this query is from an integration
  const associatedAccount = query.integration_id ? (instagramAccounts || []).find(acc => acc.integration_id === query.integration_id) : null;
  const replyCount = query.messages ? query.messages.filter(m => m.sender !== 'customer').length : 0;
  const hasHumanReply = query.human_response && query.human_response.length > 0;

  return (
    <div
      onClick={() => onSelect(query)}
      className={`relative p-4 cursor-pointer transition-all hover:bg-gray-50 ${isSelected ? 'bg-gray-50/50 border-l-4 border-black' : 'border-l-4 border-transparent'
        }`}
    >
      <div className={`absolute top-0 left-0 bottom-0 w-1 ${priorityColor}`} />

      <div className="flex items-start justify-between mb-2 pl-1">
        <div className="flex items-center min-w-0">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex-shrink-0 flex items-center justify-center text-black font-bold text-xs">
            {query.name ? query.name.charAt(0).toUpperCase() : 'U'}
          </div>
          <div className="ml-3 min-w-0">
            <h4 className="text-sm font-bold text-gray-900 truncate">
              {query.query?.substring(0, 30)}...
            </h4>
            <p className="text-xs text-gray-500 truncate">{query.name || 'Anonymous User'}</p>
          </div>
        </div>
        <div className="text-[10px] text-gray-400 whitespace-nowrap ml-2">
          {dayjs(query.created_at).fromNow()}
        </div>
      </div>

      <div className="pl-1">
        {(associatedAccount || query.external_integration?.instagram || query.source_url === 'https://instagram.com') && (
          <div className="flex items-center gap-1.5 mb-2 px-2 py-0.5 bg-gray-100 border border-gray-200 rounded text-[10px] font-bold text-black uppercase w-fit">
            <Instagram className="w-3 h-3" />
            <span>{associatedAccount?.account?.instagram_username || 'Instagram'}</span>
          </div>
        )}
        <p className="text-xs text-gray-600 line-clamp-2 mb-3 leading-relaxed">
          {query.query}
        </p>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${status.bg} ${status.color}`}>
              {status.icon}
              <span className="capitalize">{query.status === 'pending' ? (hasHumanReply ? 'open' : 'pending') : query.status}</span>
            </div>
            <div className="flex items-center text-[10px] text-gray-400">
              <MessageSquare className="w-3 h-3 mr-1" />
              {replyCount}
            </div>
          </div>
          <span className="text-[10px] text-gray-400 font-mono truncate ml-1">Q-{query.ticket_id || query.query_id.substring(0, 5)}</span>
        </div>
      </div>
    </div>
  );
});

const SupportInbox = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Track which query has been auto-opened to prevent infinite loop
  const autoOpenedQueryRef = useRef(null);

  const [inboxType, setInboxType] = useState('support');
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [instagramConnected, setInstagramConnected] = useState(false);
  const [checkingInstagram, setCheckingInstagram] = useState(true);

  // Use session storage for lightweight cache of query list
  const [queries, setQueries] = useState(() => {
    try {
      const cached = sessionStorage.getItem('support_inbox_cache');
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      return [];
    }
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  const [selectedQuery, setSelectedQuery] = useState(null);
  const [loadingSelectedDetail, setLoadingSelectedDetail] = useState(false);
  const [activeTab, setActiveTab] = useState('replies'); // 'replies' or 'csat'
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const [filters, setFilters] = useState({});
  const [statusFilter, setStatusFilter] = useState('All');
  const [sourceFilter, setSourceFilter] = useState('All');
  const [filterOptions, setFilterOptions] = useState(null);
  const [filterOpenSections, setFilterOpenSections] = useState({
    status: true,
    classification: true,
  });

  const [isNewQueryModalOpen, setIsNewQueryModalOpen] = useState(false);
  const [newQueryData, setNewQueryData] = useState({ name: '', email: '', query: '', mobile: '' });
  const [submittingNewQuery, setSubmittingNewQuery] = useState(false);
  const [filterCounts, setFilterCounts] = useState({
    all: 0,
    pending: 0,
    resolved: 0,
    closed: 0,
    instagram: 0
  });
  // Preserved global (unfiltered) counts — restored when switching back to All
  const [globalFilterCounts, setGlobalFilterCounts] = useState({
    all: 0, pending: 0, resolved: 0, closed: 0, instagram: 0
  });
  const [total, setTotal] = useState(0);
  const [csatScore, setCsatScore] = useState(null);
  const [csatLoading, setCsatLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const chatContainerRef = useRef(null);

  // Query Analysis Modal State
  const [queryAnalysisModal, setQueryAnalysisModal] = useState({
    show: false,
    query: null,
  });

  // Notes Modal State
  const [notesModal, setNotesModal] = useState({
    show: false,
    queryId: null,
    notes: [],
    newNote: "",
    submitting: false,
    editingNoteId: null,
    editingContent: "",
  });

  // Notes Cache with localStorage persistence
  const [notesCache, setNotesCache] = useState(() => {
    try {
      const savedCache = localStorage.getItem('supportInboxNotesCache');
      if (savedCache) {
        const parsedCache = JSON.parse(savedCache);
        return new Map(Object.entries(parsedCache));
      }
    } catch (error) {
      console.error('Failed to load notes cache from localStorage:', error);
    }
    return new Map();
  });

  // Persist notes cache to localStorage
  useEffect(() => {
    try {
      const cacheObject = Object.fromEntries(notesCache);
      localStorage.setItem('supportInboxNotesCache', JSON.stringify(cacheObject));
    } catch (error) {
      console.error('Failed to save notes cache to localStorage:', error);
    }
  }, [notesCache]);

  // Scroll to bottom when query loads or tab changes
  useEffect(() => {
    if (!loadingSelectedDetail && activeTab === 'replies' && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [loadingSelectedDetail, activeTab, selectedQuery?.query_id]);

  const statuses = [
    { name: 'All', icon: null, count: filterCounts.all },
    { name: 'Pending', icon: <div className="w-2 h-2 rounded-full bg-yellow-500 mr-2" />, count: filterCounts.pending },
    { name: 'Responded', icon: <div className="w-2 h-2 rounded-full bg-green-500 mr-2" />, count: filterCounts.resolved },
    { name: 'Closed', icon: <div className="w-2 h-2 rounded-full bg-gray-500 mr-2" />, count: filterCounts.closed },
  ];

  const sourceOptions = [
    { name: 'All', icon: null, count: filterCounts.all },
    { name: 'Instagram', icon: <div className="w-2 h-2 rounded-full bg-black mr-2" />, count: filterCounts.instagram },
  ];

  // Sync debounced search query with filters
  useEffect(() => {
    setFilters(prev => {
      if (prev.search_query === debouncedSearchQuery) return prev;
      const newFilters = { ...prev };
      if (debouncedSearchQuery) {
        newFilters.search_query = debouncedSearchQuery;
      } else {
        delete newFilters.search_query;
      }
      return newFilters;
    });
  }, [debouncedSearchQuery]);

  // Initialize data on component mount
  useEffect(() => {
    fetchFilterOptions();
    fetchQueries(1);

    const checkInstagramStatus = async () => {
      try {
        const result = await ApiService.getInstagramStatus();
        if (result.success && result.accounts && result.accounts.length > 0) {
          setInstagramAccounts(result.accounts);
          setInstagramConnected(true);
        } else {
          setInstagramAccounts([]);
          setInstagramConnected(false);
        }
      } catch (error) {
        console.error('Error checking Instagram status:', error);
        setInstagramConnected(false);
      } finally {
        setCheckingInstagram(false);
      }
    };
    checkInstagramStatus();
  }, []);

  // Auto-fetch on search query change
  const prevSearchQueryRef = useRef(filters.search_query);
  useEffect(() => {
    // Only fetch if search query specifically changed (to allow manual apply for other filters)
    if (filters.search_query !== prevSearchQueryRef.current) {
      prevSearchQueryRef.current = filters.search_query;
      // Skip initial mount check if undefined matches
      setPage(1);
      fetchQueries(1);
    }
  }, [filters.search_query]);

  // Filter Handlers
  const handleToggleFilterSection = (section) => {
    setFilterOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleUpdateFilter = (category, subCategory, value) => {
    const newFilters = { ...filters };
    if (!newFilters[category]) newFilters[category] = {};

    if (subCategory) {
      if (value === undefined || (Array.isArray(value) && value.length === 0)) {
        delete newFilters[category][subCategory];
        if (Object.keys(newFilters[category]).length === 0) delete newFilters[category];
      } else {
        newFilters[category][subCategory] = value;
      }
    } else {
      if (value === undefined || (Array.isArray(value) && value.length === 0)) {
        delete newFilters[category];
      } else {
        newFilters[category] = value;
      }
    }
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchQueries(1);
    if (window.innerWidth < 1200) setIsSidebarOpen(false);
  };

  const handleClearFilters = () => {
    setFilters({});
    setStatusFilter('All');
    setSourceFilter('All');
    setSearchQuery('');
    setPage(1);
    // Use timeout to ensure state update propagates before fetch if needed
    setTimeout(() => fetchQueries(1, false, null, {}), 0);
  };

  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    has_next: false,
    has_previous: false
  });

  const fetchQueries = async (targetPage = page, isBackground = false, statusFilters = null, overrideFilters = null) => {
    if (!isBackground) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const currentFilters = overrideFilters || filters;
      const payload = {
        page: targetPage,
        per_page: itemsPerPage,
        filters: { ...currentFilters }
      };

      // Legacy support for statusFilters argument if strictly needed, 
      // but we prefer using the filters state.
      if (statusFilters && statusFilters !== 'All') {
        // If statusFilters is passed (e.g. from legacy calls), override status
        payload.filters.status = [statusFilters.toLowerCase()];
      } else if (statusFilters === 'All') {
        delete payload.filters.status;
      }

      // Ensure search query is synced
      if (debouncedSearchQuery) {
        payload.filters.search_query = debouncedSearchQuery;
      }

      console.log('Fetching queries with payload:', payload);

      const response = await ApiService.getSupportQueries(payload);

      if (response && response.data) {
        // Process data to ensure all metadata is available at root level (consistency with SupportQueries.js)
        const newQueries = (response.data || []).map(query => ({
          ...query,
          classification: query.classification || query.nlp_data?.query_classification,
          sentiment: query.sentiment || query.nlp_data?.sentiment,
          urgency: query.urgency || query.nlp_data?.urgency,
          root_cause: query.root_cause || query.nlp_data?.root_cause
        }));
        setQueries(newQueries);

        if (response.pagination) {
          setPagination(response.pagination);
          const totalItems = response?.pagination?.total_items || 0;

          // Detect which source filter is active for this fetch
          const activeFilters = overrideFilters !== null ? overrideFilters : filters;
          const isInstagramFilter = Array.isArray(activeFilters?.external_integration) &&
            activeFilters.external_integration.includes('instagram');

          if (isInstagramFilter) {
            // Derive per-status counts from the returned data for this filtered view
            const igPending = newQueries.filter(q => q.status === 'pending').length;
            const igResponded = newQueries.filter(q => q.status === 'responded').length;
            const igClosed = newQueries.filter(q => q.status === 'closed').length;
            setFilterCounts(prev => ({
              ...prev,
              all: totalItems,
              instagram: totalItems,
              pending: igPending,
              resolved: igResponded,
              closed: igClosed,
            }));
          } else if (isBackground === false) {
            // No source filter — update the All count only (status counts come from fetchFilterOptions)
            setFilterCounts(prev => ({ ...prev, all: totalItems }));
          } else {
            // Background refresh (new query submitted)
            setFilterCounts(prev => ({
              ...prev,
              pending: prev.pending + 1,
              all: prev.all + 1,
            }));
          }
          setTotal(totalItems);
        }

        // Cache the first page for faster tab switching
        if (targetPage === 1) {
          sessionStorage.setItem('support_inbox_cache', JSON.stringify(newQueries));
        }


      }
    } catch (error) {
      console.error('Error fetching queries:', error);
      if (!isBackground) toast.error('Failed to load queries');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };


  const fetchFilterOptions = async () => {
    try {
      const response = await ApiService.getSupportFilterOptions();
      console.log('here is the response of the filter', response);
      if (response?.filters) {
        setFilterOptions(response.filters);
      }
      if (response?.status_counts) {
        const counts = response.status_counts || {};
        console.log('here is the counts', counts);
        const allCount = Number(counts?.closed || 0) + Number(counts?.responded || 0) + Number(counts?.pending || 0);
        const sourceCounts = response.source_counts || {};
        const newCounts = {
          all: total || allCount || 0,
          closed: counts?.closed || 0,
          resolved: counts?.responded || 0,
          pending: counts?.pending || 0,
          instagram: sourceCounts?.instagram || 0
        };
        setFilterCounts(newCounts);
        setGlobalFilterCounts(newCounts); // preserve for restoring when switching back to All
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  }



  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery]);

  const handlePageChange = (pageNum) => {
    setPage(pageNum);
    setLoading(true);
    fetchQueries(pageNum);
  };

  const handlePrevPage = () => {
    setPage(prev => Math.max(prev - 1, 1));
    setLoading(true);
    fetchQueries(page - 1);
  };

  const handleNextPage = () => {
    setPage(prev => Math.min(prev + 1, pagination.total_pages));
    setLoading(true);
    fetchQueries(page + 1);
  };

  // Fetch data when page changes
  useEffect(() => {
    if (refreshing) {
      fetchQueries(page, true);
    }
  }, [refreshing]);

  useEffect(() => {
    fetchQueries(page);
    fetchFilterOptions();
  }, []);



  const fetchCsatForQuery = async (queryId) => {
    setCsatLoading(true);
    setCsatScore(null);
    try {
      const response = await ApiService.getQueryCsat(queryId);
      if (response.success) {
        setCsatScore(response.data || null);
      }
    } catch (error) {
      console.error('Error fetching CSAT:', error);
    } finally {
      setCsatLoading(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedQuery) return;
    try {
      const response = await ApiService.updateSupportQuery([selectedQuery.query_id], newStatus);
      if (response.success || response.modified_count > 0) {
        setSelectedQuery({ ...selectedQuery, status: newStatus });
        setQueries(queries.map(q => q.query_id === selectedQuery.query_id ? { ...q, status: newStatus } : q));
        toast.success(`Status updated to ${newStatus}`);
        setFilterCounts({ ...filterCounts, resolved: filterCounts?.resolved + 1, pending: filterCounts?.pending - 1 })
      } else {
        toast.error(response.message || 'Failed to update status');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update status');
    }
  };
  console.log('here is the selected queries', selectedQuery);

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedQuery) return;
    setSendingReply(true);
    const theReply = replyText;
    try {
      const result = await ApiService.submitManualResponse(
        selectedQuery.query_id,
        selectedQuery.customer_email,
        replyText
      );
      if (result.success) {
        toast.success('Reply sent!');
        setReplyText('');
        // Refresh the current query to see new message using detail endpoint
        try {
          const detailResult = await ApiService.getSupportQueryDetail(selectedQuery.query_id);

          if (detailResult.success && detailResult.data) {
            const currentSelectedQuery = { ...selectedQuery };
            const messages = [
              ...(currentSelectedQuery?.messages || []),
              {
                sender: "support",
                content: theReply,
                timestamp: new Date().toISOString()
              }
            ];

            const updated = { ...currentSelectedQuery, messages };
            setSelectedQuery(updated);
            const findIndex = queries?.findIndex((query) => query?.query_id === updated?.query_id);
            const list = [...queries];
            list[findIndex] = updated;
            setQueries(list);
          }
        } catch (err) {
          console.error('Error refreshing after reply:', err);
        }
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const getStatusStyle = (query) => {
    const hasHumanReply = query.human_response && query.human_response.length > 0;
    if (query.status === 'closed') return { color: 'text-gray-600', bg: 'bg-gray-100', icon: <XCircle className="w-3.5 h-3.5 mr-1" /> };
    if (query.status === 'responded' || query.status === 'resolved') return { color: 'text-green-600', bg: 'bg-green-100', icon: <CheckCircle className="w-3.5 h-3.5 mr-1" /> };
    if (!hasHumanReply) return { color: 'text-gray-700', bg: 'bg-gray-100', icon: <Mail className="w-3.5 h-3.5 mr-1" /> };
    return { color: 'text-yellow-600', bg: 'bg-yellow-100', icon: <Clock className="w-3.5 h-3.5 mr-1" /> };
  };

  const getPriorityColor = (q) => {
    const level = q.priority || q.urgency?.level;
    switch (level?.toLowerCase()) {
      case 'critical':
      case 'urgent': return 'bg-purple-500';
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-gray-400';
      default: return 'bg-gray-400';
    }
  };

  const handleQuerySelect = useCallback(async (query) => {
    if (!query || !query.query_id) return;

    // OPTIMIZATION: Set selected query immediately using available data from the list
    // This makes the UI feel much faster as it doesn't wait for the detail fetch
    setSelectedQuery(prev => {
      // Only override if it's a different query or if the new one has more data
      if (prev?.query_id === query.query_id) return prev;
      return query;
    });

    setShowMobileDetail(true);

    // Check if we already have the full messages (e.g. from a previous fetch or cache)
    if (query.messages && query.messages.length > 0) {
      setLoadingSelectedDetail(false);
      return;
    }

    setLoadingSelectedDetail(true);

    try {
      const result = await ApiService.getSupportQueryDetail(query.query_id);

      if (result && result.success && result.data) {
        // Process data
        const fullQuery = {
          ...result.data,
          messages: result.data.messages || [],
          classification: result.data.classification || result.data.nlp_data?.query_classification,
          sentiment: result.data.sentiment || result.data.nlp_data?.sentiment,
          urgency: result.data.urgency || result.data.nlp_data?.urgency,
          root_cause: result.data.root_cause || result.data.nlp_data?.root_cause
        };

        // ... existing message processing logic
        const initialQueryMessage = {
          sender: 'customer',
          content: fullQuery.query,
          timestamp: fullQuery.created_at,
        };

        const hasInitialMessage = fullQuery.messages.some(
          (m) => m.content === initialQueryMessage.content && m.sender === 'customer'
        );

        if (!hasInitialMessage) {
          fullQuery.messages.unshift(initialQueryMessage);
        }

        if (fullQuery.ai_response) {
          fullQuery.messages.push({
            sender: 'ai',
            content: fullQuery.ai_response,
            timestamp: new Date().toISOString(),
          });
        }

        if (fullQuery.human_response && Array.isArray(fullQuery.human_response)) {
          fullQuery.human_response.forEach(response => {
            fullQuery.messages.push({
              sender: 'support',
              content: response,
              timestamp: new Date().toISOString(),
            });
          });
        }

        setSelectedQuery(fullQuery);

        setQueries(prevQueries =>
          prevQueries.map(q => (q.query_id === query.query_id ? fullQuery : q))
        );
      } else {
        toast.error(result?.message || 'Could not load query details.');
      }
    } catch (error) {
      console.error('Error fetching query detail:', error);
      // If it fails, we still have the initial 'query' data in selectedQuery
    } finally {
      setLoadingSelectedDetail(false);
    }
  }, []);


  // Auto-open query from URL parameter (e.g., from CSAT Scores page)
  useEffect(() => {
    const queryIdFromUrl = searchParams.get('search_query');

    // Only proceed if we have a query ID and haven't already auto-opened it
    if (queryIdFromUrl && queryIdFromUrl !== autoOpenedQueryRef.current && queries.length > 0 && !loading) {
      // Find the query in the current list
      const queryToOpen = queries.find(q => q.query_id === queryIdFromUrl);

      if (queryToOpen) {
        // Query found in current list, select it
        autoOpenedQueryRef.current = queryIdFromUrl;
        handleQuerySelect(queryToOpen);
      } else if (!selectedQuery) {
        // Query not in current list, fetch it directly (only if nothing is selected)
        autoOpenedQueryRef.current = queryIdFromUrl;
        const fetchSpecificQuery = async () => {
          try {
            const result = await ApiService.getSupportQueryDetail(queryIdFromUrl);
            if (result && result.success && result.data) {
              // Process data to ensure all metadata is available at root level (consistency with SupportQueries.js)
              const queryWithMeta = {
                ...result.data,
                classification: result.data.classification || result.data.nlp_data?.query_classification,
                sentiment: result.data.sentiment || result.data.nlp_data?.sentiment,
                urgency: result.data.urgency || result.data.nlp_data?.urgency,
                root_cause: result.data.root_cause || result.data.nlp_data?.root_cause
              };
              handleQuerySelect(queryWithMeta);
            }
          } catch (error) {
            console.error('Error fetching specific query:', error);
            toast.error('Could not find the requested query');
          }
        };
        fetchSpecificQuery();
      }
    }

    // Reset the ref when URL parameter is removed
    if (!queryIdFromUrl && autoOpenedQueryRef.current) {
      autoOpenedQueryRef.current = null;
    }
  }, [searchParams, queries, loading, selectedQuery, handleQuerySelect]);

  // Auto-select first query on desktop initial load or after refreshing/filtering
  useEffect(() => {
    const queryIdFromUrl = searchParams.get('search_query');

    // Only auto-select on desktop if:
    // 1. Not loading
    // 2. We have queries
    // 3. No query is currently selected
    // 4. There's no specific search_query in the URL (to avoid overriding deep links)
    // 5. We are on desktop (xl breakpoint in Tailwind is usually 1200px)
    if (
      !loading &&
      queries.length > 0 &&
      !selectedQuery &&
      !queryIdFromUrl &&
      window.innerWidth >= 1200
    ) {
      const firstQuery = queries[0];
      handleQuerySelect(firstQuery);
    }
  }, [queries, loading, selectedQuery, searchParams, handleQuerySelect]);

  useEffect(() => {
    if (selectedQuery && activeTab === 'csat') {
      fetchCsatForQuery(selectedQuery.query_id);
    }
  }, [selectedQuery, activeTab]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Pending' },
      responded: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Responded' },
      resolved: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', label: 'Resolved' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2.5 py-1 rounded-lg ${config.bg} ${config.text} text-xs font-medium border ${config.border} whitespace-nowrap`}>
        {config.label}
      </span>
    );
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    // Assuming you have a toast notification system
    if (typeof toast !== 'undefined') {
      toast.success(`${label} copied`);
    }
  };

  // ========== HELPER FUNCTIONS FOR QUERY ANALYSIS ==========

  // Sentiment emoji mapping
  const SENTIMENT_EMOJIS = {
    positive: '😊',
    neutral: '😐',
    negative: '😠',
    very_positive: '🤩',
    very_negative: '😡',
    frustrated: '😤',
    confused: '😕',
    satisfied: '😌',
    angry: '😠',
    happy: '😄',
  };

  const getSentimentEmoji = (sentiment) => {
    if (!sentiment) return '😐';
    const sentimentStr = typeof sentiment === 'string' ? sentiment : String(sentiment);
    const normalized = sentimentStr.toLowerCase().replace(/\s+/g, '_');
    return SENTIMENT_EMOJIS[normalized] || '😐';
  };

  // Urgency configuration
  const URGENCY_CONFIG = {
    critical: {
      color: 'bg-red-100 text-red-800 border-red-300',
      icon: '🔴',
      label: 'Critical'
    },
    high: {
      color: 'bg-orange-100 text-orange-800 border-orange-300',
      icon: '🟠',
      label: 'High'
    },
    medium: {
      color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      icon: '🟡',
      label: 'Medium'
    },
    low: {
      color: 'bg-green-100 text-green-800 border-green-300',
      icon: '🟢',
      label: 'Low'
    },
    default: {
      color: 'bg-gray-100 text-gray-800 border-gray-300',
      icon: '⚪',
      label: 'Normal'
    },
  };

  const getUrgencyConfig = (urgency) => {
    if (!urgency) return URGENCY_CONFIG.default;
    const urgencyStr = typeof urgency === 'string' ? urgency : String(urgency);
    const normalized = urgencyStr.toLowerCase();
    return URGENCY_CONFIG[normalized] || URGENCY_CONFIG.default;
  };

  // Category colors
  const CATEGORY_COLORS = {
    billing: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    technical: 'bg-gray-100 text-gray-800 border-gray-200',
    account: 'bg-purple-100 text-purple-800 border-purple-200',
    product: 'bg-green-100 text-green-800 border-green-200',
    support: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    feedback: 'bg-gray-200 text-pink-800 border-gray-300',
    complaint: 'bg-red-100 text-red-800 border-red-200',
    inquiry: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    default: 'bg-gray-100 text-gray-800 border-gray-200',
  };

  const getCategoryColor = (category) => {
    if (!category) return CATEGORY_COLORS.default;
    const categoryStr = typeof category === 'string' ? category : String(category);
    const normalized = categoryStr.toLowerCase().replace(/\s+/g, '_');
    return CATEGORY_COLORS[normalized] || CATEGORY_COLORS.default;
  };

  // Intent colors
  const INTENT_COLORS = {
    purchase: 'bg-green-100 text-green-800 border-green-200',
    refund: 'bg-orange-100 text-orange-800 border-orange-200',
    complaint: 'bg-red-100 text-red-800 border-red-200',
    inquiry: 'bg-gray-100 text-gray-800 border-gray-200',
    feedback: 'bg-purple-100 text-purple-800 border-purple-200',
    help: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    cancel: 'bg-rose-100 text-rose-800 border-rose-200',
    update: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    default: 'bg-gray-100 text-gray-800 border-gray-200',
  };

  const getIntentColor = (intent) => {
    if (!intent) return INTENT_COLORS.default;
    const intentStr = typeof intent === 'string' ? intent : String(intent);
    const normalized = intentStr.toLowerCase().replace(/\s+/g, '_');
    return INTENT_COLORS[normalized] || INTENT_COLORS.default;
  };

  // Format label helper
  // Format label helper
  const formatLabel = (text) => {
    if (!text) return '';
    const textStr = typeof text === 'string' ? text : String(text);
    return textStr
      .split(/[-_\s]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Check if the currently selected query's account is disconnected
  const associatedAccount = selectedQuery?.integration_id 
    ? (instagramAccounts || []).find(acc => acc.integration_id === selectedQuery.integration_id) 
    : null;
  const isDisconnected = associatedAccount?.overall_status === 'disconnected' || associatedAccount?.overall_status === 'token_expired';

  // ========== HELPER FUNCTIONS FOR NOTES MANAGEMENT ==========

  const getCurrentUserName = () => {
    // This is a placeholder - you should integrate with your auth system
    // For example, if using Clerk: return user?.fullName || user?.firstName || 'Current User'
    return "Support Agent";
  };

  // Notes CRUD Operations
  const openNotesModal = async (queryId) => {
    try {
      console.log(`Opening notes modal for query ${queryId}`);

      let existingNotes = [];
      const cachedNotes = notesCache.get(queryId);

      if (cachedNotes && cachedNotes.length > 0) {
        console.log(`Found ${cachedNotes.length} cached notes for query ${queryId}`);
        existingNotes = cachedNotes;
      } else {
        // Extract from local queries state
        const query = queries.find(q => q.query_id === queryId);

        const processNote = (noteData, index = 0) => {
          if (!noteData) return null;

          if (typeof noteData === 'string') {
            return {
              id: `note_${queryId}_${index}_${Date.now()}`,
              note: noteData,
              added_by: getCurrentUserName(),
              added_at: new Date().toISOString()
            };
          } else if (typeof noteData === 'object' && noteData.note) {
            return {
              id: noteData.id || `note_${queryId}_${index}_${Date.now()}`,
              note: noteData.note,
              added_by: noteData.added_by || getCurrentUserName(),
              added_at: noteData.added_at || new Date().toISOString(),
              updated_at: noteData.updated_at
            };
          }
          return null;
        };

        if (query?.notes) {
          if (Array.isArray(query.notes)) {
            existingNotes = query.notes
              .map((note, index) => processNote(note, index))
              .filter(note => note !== null);
          } else {
            const processedNote = processNote(query.notes, 0);
            if (processedNote) existingNotes = [processedNote];
          }
        }

        if (existingNotes.length > 0) {
          setNotesCache(prevCache => {
            const newCache = new Map(prevCache);
            newCache.set(queryId, existingNotes);
            return newCache;
          });
        }
      }

      setNotesModal({
        show: true,
        queryId: queryId,
        notes: existingNotes,
        newNote: "",
        submitting: false,
        editingNoteId: null,
        editingContent: "",
      });
    } catch (error) {
      console.error("Error opening notes modal:", error);
      setNotesModal({
        show: true,
        queryId: queryId,
        notes: [],
        newNote: "",
        submitting: false,
        editingNoteId: null,
        editingContent: "",
      });
    }
  };

  const handleAddNote = async () => {
    if (!notesModal.newNote.trim()) {
      toast.error("Please enter a note");
      return;
    }

    try {
      setNotesModal((prev) => ({ ...prev, submitting: true }));

      const result = await ApiService.addNoteToSupportQuery(notesModal.queryId, notesModal.newNote);

      if (result.success) {
        toast.success("Note added successfully!");

        const newNoteObj = {
          id: `temp_${Date.now()}_${Math.random()}`,
          note: notesModal.newNote,
          added_by: getCurrentUserName(),
          added_at: new Date().toISOString(),
        };

        const existingCachedNotes = notesCache.get(notesModal.queryId) || [];
        const updatedCachedNotes = [...existingCachedNotes, newNoteObj];

        setNotesCache(prevCache => {
          const newCache = new Map(prevCache);
          newCache.set(notesModal.queryId, updatedCachedNotes);
          return newCache;
        });

        setNotesModal((prev) => ({
          ...prev,
          notes: [...prev.notes, newNoteObj],
          newNote: "",
          submitting: false,
        }));

        setQueries(prevQueries =>
          prevQueries.map(query => {
            if (query.query_id === notesModal.queryId) {
              const updatedNotes = query.notes ?
                (Array.isArray(query.notes) ? [...query.notes, newNoteObj] : [query.notes, newNoteObj]) :
                [newNoteObj];
              return {
                ...query,
                notes: updatedNotes
              };
            }
            return query;
          })
        );
      } else {
        toast.error("Failed to add note");
      }
    } catch (error) {
      console.error("Add note error:", error);
      toast.error(error.message || "Failed to add note");
    } finally {
      setNotesModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  const handleEditNote = (noteId, currentContent) => {
    setNotesModal(prev => ({
      ...prev,
      editingNoteId: noteId,
      editingContent: currentContent
    }));
  };

  const handleSaveEditNote = async (noteId) => {
    if (!notesModal.editingContent.trim()) {
      toast.error("Note content cannot be empty");
      return;
    }

    try {
      setNotesModal((prev) => ({ ...prev, submitting: true }));

      const updatedNotes = notesModal.notes.map(note =>
        note.id === noteId
          ? { ...note, note: notesModal.editingContent, updated_at: new Date().toISOString() }
          : note
      );

      await ApiService.addNoteToSupportQuery(notesModal.queryId, updatedNotes);

      setNotesCache(prevCache => {
        const newCache = new Map(prevCache);
        newCache.set(notesModal.queryId, updatedNotes);
        return newCache;
      });

      setNotesModal((prev) => ({
        ...prev,
        notes: updatedNotes,
        editingNoteId: null,
        editingContent: "",
        submitting: false,
      }));

      toast.success("Note updated successfully!");
    } catch (error) {
      console.error("Update note error:", error);
      toast.error(error.message || "Failed to update note");
    } finally {
      setNotesModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      setNotesModal((prev) => ({ ...prev, submitting: true }));

      const updatedNotes = notesModal.notes.filter(note => note.id !== noteId);

      await ApiService.addNoteToSupportQuery(notesModal.queryId, updatedNotes);

      setNotesCache(prevCache => {
        const newCache = new Map(prevCache);
        newCache.set(notesModal.queryId, updatedNotes);
        return newCache;
      });

      setNotesModal((prev) => ({
        ...prev,
        notes: updatedNotes,
        submitting: false,
      }));

      toast.success("Note deleted successfully!");
    } catch (error) {
      console.error("Delete note error:", error);
      toast.error(error.message || "Failed to delete note");
    } finally {
      setNotesModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  const handleCancelEdit = () => {
    setNotesModal(prev => ({
      ...prev,
      editingNoteId: null,
      editingContent: ""
    }));
  };

  console.log('here is the selectedQuery', selectedQuery);


  return (
    <div className="flex h-[calc(100vh-20px)] md:h-[calc(100vh-100px)] bg-white md:rounded-2xl shadow-xl overflow-hidden border border-gray-200 relative">

      {/* Inbox Sidebar Overlay for Mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 xl:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Status & Priority */}
      <div className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-gray-50 border-r border-gray-200 transform transition-transform duration-300 xl:relative xl:translate-x-0
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50/50">
            <h1 className="text-xl font-bold text-gray-900">Filters</h1>
            <button onClick={() => setIsSidebarOpen(false)} className="xl:hidden p-1">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className='p-3'>
            <div className="mb-2 border-b border-gray-100">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Search
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search queries..."
                  value={filters.search_query || ''}
                  onChange={(e) => handleUpdateFilter('search_query', null, e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-gray-500 focus:border-gray-500 transition-colors"
                />
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
            </div>

            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Source</h3>
            <div className="space-y-1 mb-4">
              {sourceOptions.map((source) => (
                <button
                  key={source.name}
                  onClick={() => {
                    setSourceFilter(source.name);
                    const newFilters = { ...filters };
                    if (source.name === 'All') {
                      delete newFilters.external_integration;
                      // Restore global status counts when going back to All
                      setFilterCounts(globalFilterCounts);
                    } else if (source.name === 'Instagram') {
                      newFilters.external_integration = ['instagram'];
                    }
                    setFilters(newFilters);
                    fetchQueries(1, false, null, newFilters);
                    if (window.innerWidth < 1200) setIsSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${sourceFilter === source.name
                    ? 'bg-gray-50 text-gray-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                  <div className="flex items-center">
                    {source.icon}
                    {source.name}
                  </div>
                  <span className={`text-xs ${sourceFilter === source.name ? 'text-black' : 'text-gray-400'}`}>
                    {source.count}
                  </span>
                </button>
              ))}
            </div>

            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Status</h3>
            <div className="space-y-1">
              {statuses.map((status) => (
                <button
                  key={status.name}
                  onClick={() => {
                    setStatusFilter(status.name);
                    const newFilters = { ...filters };
                    if (status.name === 'All') {
                      delete newFilters.status;
                    } else {
                      newFilters.status = [status.name.toLowerCase()];
                    }
                    setFilters(newFilters);
                    fetchQueries(1, false, null, newFilters);
                    if (window.innerWidth < 1200) setIsSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${statusFilter === status.name
                    ? 'bg-gray-50 text-gray-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                  <div className="flex items-center">
                    {status.icon}
                    {status.name}
                  </div>
                  <span className={`text-xs ${statusFilter === status.name ? 'text-black' : 'text-gray-400'}`}>
                    {status.count}
                  </span>
                </button>
              ))}
            </div>
          </div>


          <FilterContent
            filterOptions={filterOptions}
            filters={filters}
            onUpdateFilter={handleUpdateFilter}
            openSections={filterOpenSections}
            onToggleSection={handleToggleFilterSection}
            status={false}
            search={false}
          />

          <div className="p-4 border-t border-gray-200 bg-gray-50 flex space-x-3">
            <button
              onClick={handleClearFilters}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear
            </button>
            <button
              onClick={handleApplyFilters}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition-colors shadow-sm"
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Query List Column */}
      <div className={`
        ${showMobileDetail ? 'hidden' : 'flex'} 
        xl:flex w-full xl:w-80 2xl:w-96 border-r border-gray-200 flex-col bg-white h-[100vh-80px] xl:h-full min-w-0
      `}>
        <div className="p-3 md:p-4 border-b border-gray-200 bg-white sticky top-0 z-10 flex-shrink-0">
          <div className="flex items-center justify-between gap-2 mb-3 md:mb-4">
            <div className="flex items-center gap-2"></div>
            <div className="flex items-center gap-1">
                  <button
                    onClick={() => fetchQueries(true, true)}
                    disabled={refreshing}
                    className={`p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all ${refreshing ? 'animate-spin text-gray-700' : ''}`}
                    title="Fetch Latest"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button onClick={() => setIsSidebarOpen(true)} className="xl:hidden p-1.5 bg-gray-100 rounded-lg text-gray-600">
                    <Menu className="w-5 h-5" />
                  </button>
            </div>
          </div>
            <button
              onClick={() => setIsNewQueryModalOpen(true)}
              className="w-full flex items-center justify-center gap-2 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors shadow-sm"
            >
              <Plus className="h-4 w-4" /> New Query
            </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 flex flex-col">
              {loading ? (
            <div className="p-12 text-center">
              <div className="w-10 h-10 border-4 border-gray-200 border-t-black rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-500 text-xs font-medium">Loading inbox...</p>
            </div>
          ) : queries.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-900 font-medium text-sm">No queries found</p>
              <p className="text-gray-500 text-xs mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {queries.map((query) => (
                <QueryItem
                  key={query.query_id}
                  query={query}
                  isSelected={selectedQuery?.query_id === query.query_id}
                  onSelect={handleQuerySelect}
                  getStatusStyle={getStatusStyle}
                  getPriorityColor={getPriorityColor}
                  instagramAccounts={instagramAccounts}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.total_items > 0 && (
            <div className="mt-auto border-t border-gray-100 bg-gray-50/50 px-2 py-4">
              <div className="flex flex-col items-center gap-3">
                <div className="flex items-center space-x-1">
                  <button
                    onClick={handlePrevPage}
                    disabled={page === 1 || loading}
                    className="p-1.5 text-gray-600 hover:bg-gray-200 rounded-md disabled:opacity-30"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  <div className="flex items-center space-x-1">
                    {[...Array(pagination.total_pages)].map((_, index) => {
                      const pageNum = index + 1;
                      const isActive = pageNum === page;
                      const shouldShow = pageNum === 1 || pageNum === pagination.total_pages || (pageNum >= page - 1 && pageNum <= page + 1);

                      if (shouldShow) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loading}
                            className={`w-7 h-7 flex items-center justify-center text-[10px] font-bold rounded-md transition-all ${isActive
                              ? 'bg-black text-white shadow-md'
                              : 'text-gray-600 hover:bg-gray-200'
                              } ${loading ? 'opacity-50' : ''}`}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (pageNum === page - 2 || pageNum === page + 2) {
                        return <span key={pageNum} className="text-gray-400 text-[10px]">...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={page === pagination.total_pages || loading}
                    className="p-1.5 text-gray-600 hover:bg-gray-200 rounded-md disabled:opacity-30"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
                <span className="text-[10px] text-gray-400 font-medium">
                  {(page - 1) * itemsPerPage + 1}-{Math.min(page * itemsPerPage, pagination.total_items)} of {pagination.total_items}
                </span>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Query Detail View */}
        <div className={`
          ${showMobileDetail ? 'flex' : 'hidden'} 
          xl:flex flex-1 flex-col bg-white h-full relative min-w-0
        `}>
        {selectedQuery ? (
          <>
            {/* Detail Header */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">

              {/* ================= TOP SECTION ================= */}
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">

                  {/* LEFT SIDE */}
                  <div className="flex items-center gap-3 min-w-0">

                    {/* Back Button (Mobile) */}
                    <button
                      onClick={() => setShowMobileDetail(false)}
                      className="xl:hidden p-2 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition"
                    >
                      <ArrowLeft className="w-5 h-5 text-gray-700" />
                    </button>

                    {/* Avatar */}
                    <div className="w-11 h-11 rounded-full bg-gray-800 text-white flex items-center justify-center font-bold text-lg shadow ring-2 ring-white">
                      {selectedQuery?.name?.[0]?.toUpperCase() || "E"}
                    </div>

                    {/* Name + Ticket */}
                    <div className="min-w-0">
                      <div className="flex items-center flex-wrap gap-2">
                        <h2 className="text-lg font-semibold text-gray-900 truncate">
                          {selectedQuery?.name || "Anonymous User"}
                        </h2>
                      </div>
                    </div>
                  </div>

                  {/* DESKTOP ACTION */}
                  <div className="hidden md:flex items-center gap-2">
                    {selectedQuery?.external_integration?.instagram && (
                      <button
                        onClick={() => {
                          const ig = selectedQuery.external_integration.instagram;
                          if (ig.integration_id && ig.conversation_id) {
                            navigate(`/integrations/instagram/${ig.integration_id}?tab=inbox&conversation_id=${ig.conversation_id}`);
                          }
                        }}
                        className="flex items-center gap-2 px-4 py-2.5 bg-black text-white rounded-xl text-sm font-semibold hover:bg-gray-800 active:bg-pink-700 transition shadow-sm"
                      >
                        <Instagram className="w-4 h-4" />
                        Open Conversation
                      </button>
                    )}
                    
                    {selectedQuery?.status !== "responded" &&
                      selectedQuery?.status !== "resolved" && (
                        <button
                          onClick={() => handleUpdateStatus("responded")}
                          className="flex items-center gap-2 px-4 py-2.5 bg-black text-white rounded-xl text-sm font-semibold hover:bg-gray-800 active:bg-gray-900 transition shadow-sm"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Mark Responded
                        </button>
                      )}
                  </div>
                </div>

                {/* ================= CONTACT INFO ================= */}
                <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:gap-4">

                  {/* Email */}
                  {selectedQuery?.customer_email && (
                    <div className="flex items-center gap-2 text-sm text-gray-700 min-w-0">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <span className="truncate font-medium">
                        {selectedQuery.customer_email}
                      </span>
                    </div>
                  )}

                  {/* Phone */}
                  {selectedQuery?.mobile && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Phone className="w-4 h-4 text-gray-500" />
                      <span className="font-medium">
                        {selectedQuery.mobile}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* ================= STATUS BAR ================= */}
              <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
                <div className="flex items-center justify-between flex-wrap gap-2">

                  {/* Status + Time */}
                  <div className="flex items-center gap-2 flex-wrap justify-between w-full">
                    {getStatusBadge(selectedQuery?.status)}

                    {selectedQuery?.created_at && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Clock className="w-3.5 h-3.5" />
                        <span>
                          {new Date(selectedQuery.created_at).toLocaleString("en-US", {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    )}


                    <span className="px-2 py-0.5 text-xs font-mono rounded-md bg-gray-50 text-gray-700 border border-gray-100">
                      #{selectedQuery?.ticket_id || selectedQuery?.query_id?.slice(0, 8)}
                    </span>
                  </div>

                  {/* MOBILE ACTION */}
                  {selectedQuery?.status !== "responded" &&
                    selectedQuery?.status !== "resolved" && (
                      <button
                        onClick={() => handleUpdateStatus("responded")}
                        className="md:hidden flex items-center gap-1.5 px-3 py-1.5 bg-black text-white rounded-lg text-xs font-semibold hover:bg-gray-800 active:bg-gray-900 transition"
                      >
                        <CheckCircle className="w-3.5 h-3.5" />
                        Mark Responded
                      </button>
                    )}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="px-6 border-b border-gray-200 bg-white flex-shrink-0">
              <div className="flex space-x-6 md:space-x-8">
                <button
                  onClick={() => setActiveTab('replies')}
                  className={`py-3 md:py-4 text-sm font-semibold border-b-2 transition-all ${activeTab === 'replies'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  Replies
                </button>
                <button
                  onClick={() => setActiveTab('csat')}
                  className={`py-3 md:py-4 text-sm font-semibold border-b-2 transition-all ${activeTab === 'csat'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  CSAT Feedback
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-50/50 min-h-0"
            >
              {loadingSelectedDetail ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                </div>
              ) : activeTab === 'replies' ? (
                <div className="max-w-4xl mx-auto space-y-6 pb-4">
                  {selectedQuery.messages && selectedQuery.messages.length > 0 ? (
                    selectedQuery.messages.map((msg, idx) => {
                      const isCustomer = msg.sender === 'customer';
                      const isAI = msg.sender === 'ai';
                      return (
                        <div key={idx} className={`flex items-start ${!isCustomer ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[10px] font-bold mt-1 shadow-sm ${isCustomer ? 'bg-gray-400' : (isAI ? 'bg-gray-700' : 'bg-indigo-600')
                            }`}>
                            {isCustomer ? (selectedQuery.name?.charAt(0).toUpperCase() || 'U') : (isAI ? 'AI' : 'CS')}
                          </div>
                          <div className={`${!isCustomer ? 'mr-3 md:mr-4' : 'ml-3 md:ml-4'} flex-1`}>
                            <div className={`p-4 md:p-5 rounded-2xl shadow-sm border ${isCustomer
                              ? 'bg-white rounded-tl-none border-gray-200'
                              : (isAI ? 'bg-gray-700 text-white border-gray-600 rounded-tr-none' : 'bg-indigo-600 text-white border-indigo-500 rounded-tr-none')
                              }`}>
                              {isAI ? (
                                <div className="prose prose-sm prose-invert max-w-none">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                                </div>
                              ) : (
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                              )}
                            </div>
                            <div className={`flex items-center mt-2 px-1 ${!isCustomer ? 'justify-end' : ''}`}>
                              {!isCustomer && isAI && <CheckCircle className="w-3 h-3 text-blue-500 mr-1" />}
                              <p className="text-[10px] text-gray-400 uppercase tracking-tighter">
                                {isAI ? 'AI Assistant • Auto-replied' : (isCustomer ? selectedQuery.name || 'Customer' : 'Support Agent')}
                                {/* {msg.timestamp && ` • ${dayjs(msg.timestamp).fromNow()}`} */}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="max-w-2xl mx-auto py-12 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <MessageSquare className="h-8 w-8 text-gray-400" />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900">No Messages Yet</h4>
                      <p className="text-sm text-gray-500 mt-2">Start the conversation by sending a reply below.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="max-w-2xl mx-auto py-8 md:py-12 pb-4">
                  {csatLoading ? (
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
                      <p className="text-gray-500 text-sm">Loading feedback...</p>
                    </div>
                  ) : csatScore ? (
                    <div className="bg-white rounded-3xl border border-gray-200 p-6 md:p-8 shadow-xl text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-yellow-50 rounded-full mb-6">
                        <Star className="w-8 h-8 md:w-10 md:h-10 text-yellow-500 fill-yellow-500" />
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Customer Satisfaction</h3>
                      <div className="flex justify-center gap-1 mb-6">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star
                            key={s}
                            className={`w-6 h-6 md:w-8 md:h-8 ${s <= csatScore.score ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`}
                          />
                        ))}
                      </div>
                      <div className="bg-gray-50 rounded-2xl p-4 md:p-6 border border-gray-100 italic text-gray-600 text-base md:text-lg">
                        &quot;{csatScore.comment || 'No specific comment provided by the customer.'}&quot;
                      </div>
                      <div className="mt-8 flex flex-col md:flex-row items-center justify-center text-xs md:text-sm text-gray-400 gap-2">
                        <Calendar className="w-4 h-4" />
                        Feedback received on {dayjs(csatScore.created_at).format('MMMM D, YYYY [at] h:mm A')}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center bg-white rounded-3xl border-2 border-dashed border-gray-200 p-8 md:p-12">
                      <div className="w-14 h-14 md:w-16 md:h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Star className="h-6 h-6 md:h-8 md:w-8 text-gray-300" />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900">No CSAT Feedback Yet</h4>
                      <p className="text-sm text-gray-500 max-w-xs mx-auto mt-2 leading-relaxed">
                        The customer hasn&apos;t provided a satisfaction rating for this support interaction yet.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Reply Input Area */}
            {selectedQuery.status !== 'closed' && (
              <div className="p-4 md:p-6 bg-white border-t border-gray-200 flex-shrink-0">
                <div className="max-w-4xl mx-auto">
                  <div className="relative">
                    <textarea
                      placeholder="Type your reply here..."
                      rows={2}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      disabled={sendingReply}
                      className="w-full pl-4 pr-12 py-3 bg-gray-50 border-gray-200 rounded-2xl text-sm focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none shadow-inner"
                    />
                    <button
                      onClick={handleSendReply}
                      disabled={sendingReply || !replyText.trim()}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-black text-white rounded-xl hover:bg-gray-800 transition-all shadow-lg active:scale-95 disabled:bg-gray-400"
                    >
                      {sendingReply ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send className="h-5 w-5" />}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-2 ml-1 flex-wrap">
                    <button
                      onClick={() => handleUpdateStatus('responded')}
                      className="text-[10px] md:text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 px-3 py-1.5 rounded-lg flex items-center transition-colors border border-gray-200"
                    >
                      <CheckCircle className="w-3 h-3 mr-1" /> Mark as Responded
                    </button>

                    {/* Query Analysis Button - Only show if analysis data exists */}
                    {(() => {
                      const hasAnalysisData =
                        // Check nested structure (Support Queries format)
                        selectedQuery?.classification?.primary_category ||
                        selectedQuery?.classification?.intent ||
                        selectedQuery?.sentiment?.emotion ||
                        selectedQuery?.sentiment?.overall ||
                        selectedQuery?.urgency?.level ||
                        selectedQuery?.root_cause?.category ||
                        selectedQuery?.root_cause?.specific_issue ||
                        // Check direct properties (alternative format)
                        selectedQuery?.classification ||
                        selectedQuery?.sentiment ||
                        selectedQuery?.urgency ||
                        // Check nlp_data structure
                        selectedQuery?.nlp_data?.primary_category ||
                        selectedQuery?.nlp_data?.intent ||
                        selectedQuery?.nlp_data?.sentiment ||
                        selectedQuery?.nlp_data?.urgency ||
                        selectedQuery?.nlp_data?.root_cause;

                      if (!hasAnalysisData) return null;

                      return (
                        <button
                          onClick={() => setQueryAnalysisModal({ show: true, query: selectedQuery })}
                          className="text-[10px] md:text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg flex items-center transition-colors border border-indigo-200"
                        >
                          <FileText className="w-3 h-3 mr-1" /> Query Analysis
                        </button>
                      );
                    })()}

                    {/* Manage Notes Button */}
                    <button
                      onClick={() => openNotesModal(selectedQuery.query_id)}
                      className="text-[10px] md:text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-lg flex items-center transition-colors border border-green-200"
                    >
                      <PlusCircle className="w-3 h-3 mr-1" /> Manage Notes
                    </button>
                  </div>

                  {/* Disconnected Account Alert */}
                  {(() => {
                    const associatedAccount = selectedQuery.integration_id ? (instagramAccounts || []).find(acc => (acc.integration_id || acc.id) === selectedQuery.integration_id) : null;
                    const isDisconnected = associatedAccount?.overall_status === 'disconnected' || 
                                          associatedAccount?.overall_status === 'token_expired' ||
                                          associatedAccount?.status === 'disconnected' ||
                                          associatedAccount?.status === 'token_expired';

                    if (isDisconnected) {
                      return (
                        <div className="flex items-center gap-3 px-4 py-3.5 bg-red-50 border border-red-100 rounded-2xl text-red-800 justify-center mt-4">
                          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                          <span className="text-xs font-bold text-center">
                            Your account is disconnected or token expired, please reconnect again.
                          </span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 p-6 md:p-12 text-center">
            <div className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-3xl shadow-lg flex items-center justify-center mb-6">
              <Mail className="h-8 w-8 md:h-10 md:w-10 text-black" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Open a query to start</h3>
            <p className="text-sm md:text-base text-gray-500 max-w-sm">
              Please select a query from the list on the left to view the full conversation and user analysis.
              If there are no queries, you can create a new one to get started.
            </p>
          </div>
        )}
      </div>

      {/* Query Analysis Modal */}
      {queryAnalysisModal.show && queryAnalysisModal.query && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setQueryAnalysisModal({ show: false, query: null })}></div>
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50/50">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                    <FileText className="h-6 w-6 mr-3 text-indigo-500" />
                    Query Analysis
                  </h2>
                  <button
                    onClick={() => setQueryAnalysisModal({ show: false, query: null })}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-gray-600 mt-2">Detailed analysis of customer query</p>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Category */}
                  {(() => {
                    const classification = queryAnalysisModal.query?.classification;
                    if (!classification) return null;

                    const categoryValue = classification.primary_category || classification.category || (typeof classification === 'string' ? classification : null);
                    if (!categoryValue) return null;

                    return (
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <div className="flex items-center mb-2">
                          <Tag className="h-4 w-4 text-gray-500 mr-2" />
                          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Primary Category</h4>
                        </div>
                        <span className={`inline-block px-3 py-1.5 rounded-lg text-sm font-medium border ${getCategoryColor(categoryValue)}`}>
                          {formatLabel(categoryValue)}
                        </span>
                      </div>
                    );
                  })()}

                  {/* Intent */}
                  {(() => {
                    const classification = queryAnalysisModal.query?.classification;
                    if (!classification) return null;

                    const intentValue = classification.intent || (typeof classification.intent === 'string' ? classification.intent : null);
                    if (!intentValue) return null;

                    return (
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <div className="flex items-center mb-2">
                          <Target className="h-4 w-4 text-gray-500 mr-2" />
                          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Customer Intent</h4>
                        </div>
                        <span className={`inline-block px-3 py-1.5 rounded-lg text-sm font-medium border ${getIntentColor(intentValue)}`}>
                          {formatLabel(intentValue)}
                        </span>
                      </div>
                    );
                  })()}

                  {/* Sentiment */}
                  {(() => {
                    const sentiment = queryAnalysisModal.query?.sentiment;
                    if (!sentiment) return null;

                    // Extract sentiment value - handle {overall, emotion, score} object
                    const sentimentValue = sentiment.emotion || sentiment.overall || (typeof sentiment === 'string' ? sentiment : null);
                    if (!sentimentValue) return null;

                    return (
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <div className="flex items-center mb-2">
                          <TrendingUp className="h-4 w-4 text-gray-500 mr-2" />
                          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Sentiment</h4>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-2xl">{getSentimentEmoji(sentimentValue)}</span>
                          <span className="text-sm font-medium text-gray-700">
                            {formatLabel(sentimentValue)}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Urgency */}
                  {(() => {
                    const urgency = queryAnalysisModal.query?.urgency;
                    if (!urgency) return null;

                    const urgencyValue = urgency.level || (typeof urgency === 'string' ? urgency : null);
                    if (!urgencyValue) return null;

                    const urgencyConfig = getUrgencyConfig(urgencyValue);
                    return (
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <div className="flex items-center mb-2">
                          <AlertTriangle className="h-4 w-4 text-gray-500 mr-2" />
                          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Urgency Level</h4>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xl">{urgencyConfig.icon}</span>
                          <span className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${urgencyConfig.color}`}>
                            {urgencyConfig.label}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Root Cause */}
                  {(() => {
                    const rootCause = queryAnalysisModal.query?.root_cause;
                    if (!rootCause) return null;

                    const rootCauseValue = rootCause.specific_issue || rootCause.category || (typeof rootCause === 'string' ? rootCause : null);
                    if (!rootCauseValue) return null;

                    return (
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 md:col-span-2">
                        <div className="flex items-center mb-2">
                          <Zap className="h-4 w-4 text-gray-500 mr-2" />
                          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Root Cause Analysis</h4>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed bg-amber-50 border border-amber-200 px-3 py-2 rounded-md">
                          {formatLabel(rootCauseValue)}
                        </p>
                      </div>
                    );
                  })()}
                </div>

                {/* No Analysis Data */}
                {(() => {
                  const hasAnyData =
                    // New/Nested structure
                    queryAnalysisModal.query?.classification?.primary_category ||
                    queryAnalysisModal.query?.classification?.intent ||
                    queryAnalysisModal.query?.sentiment?.emotion ||
                    queryAnalysisModal.query?.sentiment?.overall ||
                    queryAnalysisModal.query?.urgency?.level ||
                    queryAnalysisModal.query?.root_cause?.category ||
                    queryAnalysisModal.query?.root_cause?.specific_issue ||
                    // Direct structure
                    queryAnalysisModal.query?.classification ||
                    queryAnalysisModal.query?.sentiment ||
                    queryAnalysisModal.query?.urgency ||
                    // NLP data
                    queryAnalysisModal.query?.nlp_data?.primary_category ||
                    queryAnalysisModal.query?.nlp_data?.intent ||
                    queryAnalysisModal.query?.nlp_data?.sentiment ||
                    queryAnalysisModal.query?.nlp_data?.urgency ||
                    queryAnalysisModal.query?.nlp_data?.root_cause;

                  if (hasAnyData) return null;

                  return (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FileText className="h-8 w-8 text-gray-400" />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900">No Analysis Data Available</h4>
                      <p className="text-sm text-gray-500 mt-2">This query hasn&apos;t been analyzed yet.</p>
                    </div>
                  );
                })()}
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50/50 flex justify-end">
                <button
                  onClick={() => setQueryAnalysisModal({ show: false, query: null })}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg transition-all duration-200 transform hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Professional Notes Management Modal */}
      {notesModal.show && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full h-[90vh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50/50">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                    <MessageSquare className="h-6 w-6 mr-3 text-gray-600" />
                    Notes Management
                  </h2>
                  <button
                    onClick={() => setNotesModal({ show: false, queryId: null, notes: [], newNote: "", submitting: false, editingNoteId: null, editingContent: "" })}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-gray-600 mt-2">Manage all notes for this support query</p>
              </div>

              {/* Side-by-side content */}
              <div className="flex-1 flex flex-col md:flex-row gap-6 overflow-hidden p-6">
                {/* Left: Add New Note (fixed width on md+, full width on small screens) */}
                <div className="w-full md:w-1/3 flex-shrink-0 bg-gray-50/50 border border-gray-100 rounded-xl p-5 flex flex-col">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <PlusCircle className="h-5 w-5 mr-2 text-gray-600" />
                    Add New Note
                  </h3>

                  <textarea
                    value={notesModal.newNote}
                    onChange={(e) => setNotesModal(prev => ({ ...prev, newNote: e.target.value }))}
                    placeholder="Write your note here..."
                    rows={8}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 mb-4 flex-1"
                    disabled={notesModal.submitting}
                  />

                  <div className="flex items-center justify-between mt-2">
                    <div className="text-sm text-gray-500">Signed in as <span className="font-medium text-gray-700">{getCurrentUserName()}</span></div>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setNotesModal(prev => ({ ...prev, newNote: "" }))}
                        className="inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg transition-all duration-200"
                        disabled={notesModal.submitting}
                      >
                        Clear
                      </button>

                      <button
                        onClick={handleAddNote}
                        disabled={!notesModal.newNote.trim() || notesModal.submitting}
                        className="inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-medium text-white bg-gray-800 hover:bg-black rounded-lg transition-all duration-200 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {notesModal.submitting ? (
                          <>
                            <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white/30 border-t-white rounded-full inline-block"></div>
                            Adding...
                          </>
                        ) : (
                          <>
                            <PlusCircle className="h-4 w-4 flex-shrink-0" />
                            Add Note
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right: Existing Notes (scrollable) */}
                <div className="flex-1 flex flex-col overflow-hidden">
                  <div className="flex-shrink-0 px-4 py-2 border-b border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <MessageSquare className="h-5 w-5 mr-2 text-black-600" />
                      All Notes ({notesModal.notes.length})
                    </h3>
                  </div>

                  <div className="flex-1 overflow-y-auto px-4 py-4">
                    {notesModal.notes.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="relative mx-auto w-20 h-20 mb-4">
                          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl"></div>
                          <div className="absolute inset-2 bg-white rounded-xl flex items-center justify-center">
                            <MessageSquare className="h-8 w-8 text-gray-400" />
                          </div>
                        </div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">No Notes Yet</h4>
                        <p className="text-gray-500">Add your first note using the form on the left</p>
                      </div>
                    ) : (
                      <div className="space-y-4 pb-4">
                        {notesModal.notes.map((note, index) => (
                          <div key={note.id || index} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200">
                            <div className="p-5">
                              {/* Note Header */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                                    <User className="h-4 w-4 text-white" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {note.added_by || "Unknown User"}
                                    </p>
                                    <div className="flex items-center text-xs text-gray-500">
                                      <Calendar className="h-3 w-3 mr-1" />
                                      {note.added_at ? new Date(note.added_at).toLocaleString() : "Unknown Date"}
                                      {note.updated_at && (
                                        <span className="ml-2 text-blue-600">(Updated: {new Date(note.updated_at).toLocaleString()})</span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Note Actions */}
                                <div className="flex items-center space-x-2">
                                  {notesModal.editingNoteId === note.id ? (
                                    <>
                                      <button
                                        onClick={() => handleSaveEditNote(note.id)}
                                        disabled={!notesModal.editingContent.trim() || notesModal.submitting}
                                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200 disabled:opacity-50"
                                        title="Save"
                                      >
                                        <Save className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={handleCancelEdit}
                                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                                        title="Cancel"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => handleEditNote(note.id, note.note)}
                                        className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                                        title="Edit Note"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteNote(note.id)}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                                        title="Delete Note"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Note Content */}
                              {notesModal.editingNoteId === note.id ? (
                                <textarea
                                  value={notesModal.editingContent}
                                  onChange={(e) => setNotesModal(prev => ({ ...prev, editingContent: e.target.value }))}
                                  rows={4}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                />
                              ) : (
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                  <p className="text-gray-800 leading-relaxed whitespace-pre-wrap break-words">
                                    {note.note}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50/50 flex justify-end">
                <button
                  type="button"
                  onClick={() => setNotesModal({ show: false, queryId: null, notes: [], newNote: "", submitting: false, editingNoteId: null, editingContent: "" })}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg transition-all duration-200 transform hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Query Modal */}
      {isNewQueryModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <h3 className="text-xl font-bold text-gray-900">Create New Query</h3>
              <button onClick={() => setIsNewQueryModalOpen(false)} className="p-2 hover:bg-gray-200 rounded-xl transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!newQueryData.email || !newQueryData.query) {
                toast.error('Email and query are required');
                return;
              }
              setSubmittingNewQuery(true);
              try {
                const result = await ApiService.submitSupportQuery(
                  newQueryData.email,
                  newQueryData.query,
                  newQueryData.name || 'User',
                  newQueryData.mobile
                );
                if (result.success) {
                  toast.success('Query created successfully');
                  setIsNewQueryModalOpen(false);
                  setNewQueryData({ name: '', email: '', query: '', mobile: '' });
                  setRefreshing(true);
                  // Reload queries
                  // setPage(1);
                  // setQueries([]);
                }
              } catch (error) {
                toast.error('Failed to create query');
              } finally {
                setSubmittingNewQuery(false);
              }
            }} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Customer Name</label>
                <input
                  type="text"
                  value={newQueryData.name}
                  onChange={(e) => setNewQueryData({ ...newQueryData, name: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Customer Email *</label>
                <input
                  type="email"
                  required
                  value={newQueryData.email}
                  onChange={(e) => setNewQueryData({ ...newQueryData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                  placeholder="customer@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Mobile Number</label>
                <input
                  type="tel"
                  value={newQueryData.mobile}
                  onChange={(e) => setNewQueryData({ ...newQueryData, mobile: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  placeholder="+1234567890"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Query *</label>
                <textarea
                  required
                  rows={4}
                  value={newQueryData.query}
                  onChange={(e) => setNewQueryData({ ...newQueryData, query: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
                  placeholder="Describe the issue..."
                />
              </div>
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submittingNewQuery}
                  className="w-full py-3 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-all shadow-lg shadow-gray-500/25 disabled:bg-gray-400 disabled:shadow-none flex items-center justify-center gap-2"
                >
                  {submittingNewQuery ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Plus className="w-5 h-5" />}
                  Create Query
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportInbox;
